import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { StatusBadge } from "@/components/StatusBadge";
import { UtilityLog } from "@/components/UtilityLog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Gauge, Zap, Droplet, Loader2, ChevronRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";

const METERED = ["RAZ-J1", "RAZ-J2", "RAZ-J3", "RAZ-J4"];

const latestOf = (readings, type) => {
  const l = readings.filter((r) => r.meter_type === type).sort((a, b) => b.date.localeCompare(a.date));
  return l[0] || null;
};

const TREND_MARGIN = { top: 4, right: 2, left: 2, bottom: 0 };

const buildTrend = (readings) => {
  const byDate = {};
  readings.filter((r) => r.consumption != null).forEach((r) => {
    byDate[r.date] = byDate[r.date] || { date: r.date };
    byDate[r.date][r.meter_type] = r.consumption;
  });
  return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
};

const MiniTrend = ({ readings }) => {
  const data = buildTrend(readings);
  if (data.length < 2) {
    return <div className="h-12 flex items-center justify-center text-[10px] text-slate-300">Trend appears after 2+ readings</div>;
  }
  return (
    <div className="h-12 w-full" data-testid="mini-trend">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={TREND_MARGIN}>
          <Line type="monotone" dataKey="power" stroke="#F59E0B" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="water" stroke="#0EA5E9" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const MeterLine = ({ icon: Icon, color, label, unit, reading }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="flex items-center gap-1.5 text-slate-500">
      <Icon className="h-3.5 w-3.5" style={{ color }} /> {label}
    </span>
    {reading ? (
      <span className="font-mono-nums text-slate-800">
        {reading.value} {unit} <span className="text-slate-400 text-xs">· {reading.date}</span>
      </span>
    ) : (
      <span className="text-slate-400 text-xs italic">No readings</span>
    )}
  </div>
);

export default function MeterLogs() {
  const [sheds, setSheds] = useState(null);
  const [readingsMap, setReadingsMap] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const razza = await api.getUnits({ site: "RAZZA" });
    const metered = razza
      .filter((u) => METERED.includes(u.unit_number))
      .sort((a, b) => a.unit_number.localeCompare(b.unit_number, undefined, { numeric: true }));
    const entries = await Promise.all(metered.map((u) => api.getReadings(u.id).then((r) => [u.id, r]).catch(() => [u.id, []])));
    setReadingsMap(Object.fromEntries(entries));
    setSheds(metered);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addReading = async (body) => {
    const r = await api.addReading(selected.id, body);
    setReadingsMap((m) => ({ ...m, [selected.id]: [r, ...(m[selected.id] || [])] }));
  };
  const delReading = async (rid) => {
    const id = selected.id;
    await api.deleteReading(id, rid);
    const rs = await api.getReadings(id).catch(() => null);
    setReadingsMap((m) => ({ ...m, [id]: rs || (m[id] || []).filter((r) => r.id !== rid) }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="Meter Logs" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Gauge className="h-6 w-6 text-blue-600" /> Meter Logs
          </h2>
          <p className="text-sm text-slate-500 mt-1">Individually metered Razza sheds. Click a shed to view its history and log new Power (kWh) &amp; Water (m³) readings.</p>
        </div>

        {sheds === null ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading metered sheds...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="meter-logs-grid">
            {sheds.map((u) => {
              const rs = readingsMap[u.id] || [];
              return (
                <button
                  key={u.id}
                  data-testid={`meter-shed-${u.unit_number}`}
                  onClick={() => setSelected(u)}
                  className="text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-blue-400 hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-mono-nums font-bold text-lg text-slate-900 leading-none">{u.unit_number}</p>
                      <p className="text-xs text-slate-500 mt-1">{u.site_name} · {u.size || "—"}</p>
                    </div>
                    <StatusBadge status={u.status} />
                  </div>
                  <div className="space-y-1.5 border-t border-slate-100 pt-3">
                    <MeterLine icon={Zap} color="#F59E0B" label="Power" unit="kWh" reading={latestOf(rs, "power")} />
                    <MeterLine icon={Droplet} color="#0EA5E9" label="Water" unit="m³" reading={latestOf(rs, "water")} />
                  </div>
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">Consumption trend</span>
                      <span className="flex items-center gap-2 text-[10px]">
                        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: "#F59E0B" }} />kWh</span>
                        <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: "#0EA5E9" }} />m³</span>
                      </span>
                    </div>
                    <MiniTrend readings={rs} />
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
                    <span className="font-mono-nums">{rs.length} reading{rs.length === 1 ? "" : "s"}</span>
                    <span className="flex items-center gap-0.5 text-blue-600 font-medium">Open log <ChevronRight className="h-3.5 w-3.5" /></span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="meter-shed-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono-nums text-xl">{selected.unit_number} — Meter Log</DialogTitle>
                <DialogDescription>{selected.site_name} · {selected.size || "No size"} — record Power (kWh) and Water (m³) readings.</DialogDescription>
              </DialogHeader>
              <div className="max-h-[70vh] overflow-auto pr-1">
                <UtilityLog unitId={selected.id} readings={readingsMap[selected.id] || []} onAdd={addReading} onDelete={delReading} onInvoiced={() => { const id = selected.id; api.getReadings(id).then((rs) => setReadingsMap((m) => ({ ...m, [id]: rs }))).catch(() => {}); }} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
